/**
 * BusinessPulsePanel — compact executive health strip covering:
 *   Business Brain · Brand Brain · Campaign Pipeline · Publishing ·
 *   Operational Health.
 *
 * Patch 36.0 · Project Obsidian.
 *
 * Every cell shows a short deterministic verdict derived from
 * canonical backend data. No percentages, no invented metrics — if the
 * backend cannot produce a positive signal we render "Standing by".
 */
import { Activity, Brain, Megaphone, Palette, Rocket } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { useAkinexBriefing, flattenBriefingFindings } from "@/entities/akinex";
import { useCampaignRunSummary } from "@/entities/campaign-run";
import { useCurrentWorkspace } from "@/entities/workspace";
import { getAutopilotDashboard } from "@/features/autopilot/api";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

type Tone = "healthy" | "attention" | "alert" | "standby";

const TONE_CLASS: Record<Tone, string> = {
  healthy: "text-emerald-400",
  attention: "text-amber-400",
  alert: "text-red-400",
  standby: "text-[var(--md-fog)]",
};

interface Cell {
  key: string;
  icon: JSX.Element;
  label: string;
  verdict: string;
  tone: Tone;
}

export function BusinessPulsePanel(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? null;
  const briefingQ = useAkinexBriefing(workspaceId);
  const summaryQ = useCampaignRunSummary(workspaceId);
  const autopilotQ = useQuery({
    queryKey: ["autopilot-dashboard", workspaceId ?? ""],
    queryFn: getAutopilotDashboard,
    refetchInterval: 60_000,
    retry: false,
    enabled: !!workspaceId,
  });

  const loading =
    !workspaceId ||
    briefingQ.isLoading ||
    summaryQ.isLoading ||
    autopilotQ.isLoading;

  if (loading) {
    return (
      <Card
        className="p-5 border-white/10 obsidian-card"
        data-testid="obsidian-business-pulse"
        data-state="loading"
      >
        <PanelTitle title="Business Pulse" />
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      </Card>
    );
  }

  const findings = briefingQ.data
    ? flattenBriefingFindings(briefingQ.data)
    : [];
  const counters = summaryQ.data?.counters;

  const brainFindings = findings.filter(
    (f) => f.module === "knowledge_curator",
  );
  const opsFindings = findings.filter(
    (f) => f.module === "operations_sentinel",
  );
  const pubFindings = opsFindings.filter(
    (f) => f.key === "publishing_failed" || f.key === "publishing_retrying",
  );

  const cells: Cell[] = [
    {
      key: "business-brain",
      icon: <Brain className="h-3.5 w-3.5" aria-hidden />,
      label: "Business Brain",
      ...verdictFromBrain(brainFindings),
    },
    {
      key: "brand-brain",
      icon: <Palette className="h-3.5 w-3.5" aria-hidden />,
      label: "Brand Brain",
      // No dedicated Brand Brain module yet — reflect knowledge state
      // conservatively rather than inventing signal.
      ...verdictFromBrandBrain(brainFindings),
    },
    {
      key: "campaign-pipeline",
      icon: <Megaphone className="h-3.5 w-3.5" aria-hidden />,
      label: "Campaign Pipeline",
      ...verdictFromCampaigns(counters, autopilotQ.data?.campaigns_running),
    },
    {
      key: "publishing",
      icon: <Rocket className="h-3.5 w-3.5" aria-hidden />,
      label: "Publishing",
      ...verdictFromPublishing(pubFindings),
    },
    {
      key: "operational-health",
      icon: <Activity className="h-3.5 w-3.5" aria-hidden />,
      label: "Operational Health",
      ...verdictFromOps(opsFindings),
    },
  ];

  return (
    <Card
      className="p-5 border-white/10 obsidian-card"
      data-testid="obsidian-business-pulse"
      data-state="populated"
    >
      <PanelTitle title="Business Pulse" />
      <ul className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
        {cells.map((c) => (
          <li
            key={c.key}
            data-testid={`obsidian-pulse-${c.key}`}
            data-tone={c.tone}
            className="rounded-lg border border-white/5 bg-black/10 px-3 py-2.5"
          >
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[var(--md-fog)]">
              {c.icon}
              {c.label}
            </div>
            <div
              className={`mt-1 text-sm font-medium ${TONE_CLASS[c.tone]}`}
              data-testid={`obsidian-pulse-${c.key}-verdict`}
            >
              {c.verdict}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function verdictFromBrain(
  brainFindings: Array<{ key: string; severity: string }>,
): { verdict: string; tone: Tone } {
  const missing = brainFindings.some((f) => f.key === "brain_missing");
  const unapproved = brainFindings.some((f) => f.key === "brain_unapproved");
  const incomplete = brainFindings.some((f) => f.key === "brain_incomplete");
  if (missing) return { verdict: "Not started", tone: "alert" };
  if (unapproved) return { verdict: "Awaiting approval", tone: "attention" };
  if (incomplete) return { verdict: "Incomplete", tone: "attention" };
  return { verdict: "Healthy", tone: "healthy" };
}

function verdictFromBrandBrain(
  brainFindings: Array<{ key: string; severity: string }>,
): { verdict: string; tone: Tone } {
  const missing = brainFindings.some((f) => f.key === "brain_missing");
  const unapproved = brainFindings.some((f) => f.key === "brain_unapproved");
  if (missing || unapproved) {
    return { verdict: "Blocked by Business Brain", tone: "attention" };
  }
  return { verdict: "Aligned", tone: "healthy" };
}

function verdictFromCampaigns(
  counters?: { draft?: number; approved?: number; published?: number },
  running?: number,
): { verdict: string; tone: Tone } {
  const active = running ?? 0;
  const drafts = counters?.draft ?? 0;
  if (active > 0) {
    return {
      verdict: `${active} Active`,
      tone: drafts > 0 ? "attention" : "healthy",
    };
  }
  if (drafts > 0) return { verdict: `${drafts} in draft`, tone: "attention" };
  return { verdict: "Standing by", tone: "standby" };
}

function verdictFromPublishing(
  pubFindings: Array<{ key: string; severity: string }>,
): { verdict: string; tone: Tone } {
  const failed = pubFindings.some((f) => f.key === "publishing_failed");
  const retrying = pubFindings.some((f) => f.key === "publishing_retrying");
  if (failed) return { verdict: "Attention Needed", tone: "alert" };
  if (retrying) return { verdict: "Auto-retrying", tone: "attention" };
  return { verdict: "On track", tone: "healthy" };
}

function verdictFromOps(
  opsFindings: Array<{ severity: string }>,
): { verdict: string; tone: Tone } {
  const critical = opsFindings.filter(
    (f) => f.severity === "critical",
  ).length;
  const high = opsFindings.filter((f) => f.severity === "high").length;
  if (critical > 0) return { verdict: "Alert", tone: "alert" };
  if (high > 0) return { verdict: "Watching", tone: "attention" };
  return { verdict: "Stable", tone: "healthy" };
}

function PanelTitle({ title }: { title: string }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Activity className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
      <h3 className="text-sm font-semibold text-[var(--md-ivory-0)]">
        {title}
      </h3>
    </div>
  );
}
